/**
 * Card.tsx — Container for content on a panel.
 *
 * Composed of:
 *   <Card>
 *     <CardHeader>
 *       <CardTitle>Title</CardTitle>
 *       <CardSubtitle>Optional description</CardSubtitle>
 *     </CardHeader>
 *     <CardBody>...</CardBody>
 *   </Card>
 *
 * Or with the shortcut:
 *   <Card title="..." subtitle="..." action={<Button />}>
 *     ...
 *   </Card>
 *
 * Variants:
 *   - default: surface.panel bg
 *   - elevated: brighter + shadow (for modals / floating)
 *   - inset: darker (for sunken areas inside cards)
 *   - primary: with cyan accent border + inner glow (for hero moments)
 */

import { ReactNode, HTMLAttributes } from 'react';
import { theme } from '../../theme';

type CardVariant = 'default' | 'elevated' | 'inset' | 'primary';

interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: CardVariant;
  padding?: keyof typeof theme.space | 'none';
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}

function variantStyles(variant: CardVariant): React.CSSProperties {
  switch (variant) {
    case 'elevated':
      return {
        background: theme.surface.elevated,
        border: `1px solid ${theme.borderColor.default}`,
        boxShadow: theme.shadow.elevated,
      };
    case 'inset':
      return {
        background: theme.surface.inset,
        border: `1px solid ${theme.borderColor.subtle}`,
      };
    case 'primary':
      return {
        background: `linear-gradient(135deg, ${theme.surface.panel} 0%, rgba(0,212,255,0.04) 100%)`,
        border: `1px solid ${theme.borderColor.brand}`,
        boxShadow: theme.shadow.glowSoft,
      };
    default:
      return {
        background: theme.surface.panel,
        border: `1px solid ${theme.borderColor.default}`,
      };
  }
}

export function Card({
  variant = 'default',
  padding = 6,
  title,
  subtitle,
  action,
  children,
  style,
  ...rest
}: CardProps) {
  const pad = padding === 'none' ? '0' : theme.space[padding];
  const hasHeader = title || action;

  return (
    <div
      style={{
        borderRadius: theme.radius.lg,
        padding: pad,
        ...variantStyles(variant),
        ...style,
      }}
      {...rest}
    >
      {hasHeader && (
        <CardHeader>
          <div>
            {title && <CardTitle>{title}</CardTitle>}
            {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
          </div>
          {action}
        </CardHeader>
      )}
      {children}
    </div>
  );
}

export function CardHeader({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.space[4],
      marginBottom: theme.space[5],
      ...style,
    }}>
      {children}
    </div>
  );
}

export function CardTitle({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <h3 style={{
      fontSize: theme.fontSize.md,
      fontWeight: theme.fontWeight.semibold,
      color: theme.text.primary,
      letterSpacing: theme.letterSpacing.normal,
      margin: 0,
      lineHeight: 1.3,
      ...style,
    }}>
      {children}
    </h3>
  );
}

export function CardSubtitle({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{
      fontSize: theme.fontSize.sm,
      color: theme.text.tertiary,
      marginTop: 4,
      marginBottom: 0,
      ...style,
    }}>
      {children}
    </p>
  );
}

/* ═══════════════ CARD TABS ═══════════════ */
/* Segmented tabs, typically used in card headers for time-range pickers. */

interface CardTabsProps<T extends string> {
  tabs: readonly { key: T; label: ReactNode }[];
  active: T;
  onChange: (key: T) => void;
}

export function CardTabs<T extends string>({ tabs, active, onChange }: CardTabsProps<T>) {
  return (
    <div style={{
      display: 'flex',
      gap: 2,
      background: theme.surface.base,
      borderRadius: 7,
      padding: 2,
      border: `1px solid ${theme.borderColor.subtle}`,
      flexShrink: 0,
    }}>
      {tabs.map(t => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              padding: '4px 10px',
              fontSize: theme.fontSize.xs,
              fontWeight: theme.fontWeight.medium,
              color: isActive ? theme.text.primary : theme.text.tertiary,
              background: isActive ? theme.surface.card : 'transparent',
              border: 'none',
              borderRadius: 5,
              cursor: 'pointer',
              fontFamily: theme.font.ui,
              transition: `all ${theme.motion.fast}`,
              boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
